(() => {
  const lpmText = window.lpmText || (source => source);
  if (customElements.get('lpm-adaptive-gallery')) return;
  customElements.define('lpm-adaptive-gallery', class extends HTMLElement {
    connectedCallback() {
      this._frame = requestAnimationFrame(() => this.initialize());
    }
    disconnectedCallback() {
      cancelAnimationFrame(this._frame);
      this._initialized = false;
      this._events?.abort();
      this._resize?.disconnect();
      this._sources?.disconnect();
      this._counter?.remove();
      this._pagination?.remove();
      this._controls?.remove();
      this._dialog?.remove();
    }
    initialize() {
      this._images = Array.from(this.querySelectorAll(':scope > img'));
      this._slides = Array.from(this.querySelectorAll(':scope > img, :scope > video'));
      if (!this._images.length) return;
      this.setAttribute('aria-label', lpmText('Galería de producto'));
      this._events = new AbortController();
      const options = { signal: this._events.signal };
      const update = () => this.updateHeight();
      this.addEventListener('scroll', update, { ...options, passive: true });
      this._images.forEach(image => image.addEventListener('load', update, options));
      this._slides.filter(slide => slide.tagName === 'VIDEO').forEach(video => video.addEventListener('loadedmetadata', update, options));
      document.addEventListener('visibilitychange', () => { if (document.hidden) this._slides.forEach(slide => {if (slide.tagName === 'VIDEO') slide.pause();}); }, options);
      this._resize = new ResizeObserver(update);
      this._resize.observe(this);
      // Existing variant and thumbnail handlers replace the hero's src. Bring
      // that image into view and measure it again when the new source loads.
      this._sources = new MutationObserver(records => {
        if (records.some(record => record.attributeName === 'src')) this.updateSources();
        if (matchMedia('(max-width: 749px)').matches) this.scrollLeft = 0;
        update();
      });
      this._sources.observe(this._images[0], { attributes: true, attributeFilter: ['src', 'srcset'] });
      this._counter = document.createElement('p');
      this._counter.className = 'lpm-gallery-counter';
      this._counter.setAttribute('role', 'status');
      this._counter.setAttribute('aria-live', 'polite');
      this.insertAdjacentElement('afterend', this._counter);
      if (this._slides.length > 1) {
        this._pagination = document.createElement('div');
        this._pagination.className = 'lpm-gallery-pagination';
        this._pagination.setAttribute('aria-hidden', 'true');
        this.insertAdjacentElement('afterend', this._pagination);
      }
      if (this.hasAttribute('data-lpm-editorial-gallery')) {
        this._controls = document.createElement('div');
        this._controls.className = 'lpm-gallery-controls';
        this._previous = document.createElement('button');
        this._next = document.createElement('button');
        this._position = document.createElement('span');
        this._previous.type = this._next.type = 'button';
        this._previous.textContent = lpmText('Anterior'); this._next.textContent = lpmText('Siguiente');
        this._previous.setAttribute('aria-label', lpmText('Imagen anterior'));
        this._next.setAttribute('aria-label', lpmText('Imagen siguiente'));
        const move = delta => {
          const current = this._slides.findIndex(slide => slide.hasAttribute('data-lpm-current'));
          const target = this._slides[current + delta];
          if (target) this.showMedia(target);
        };
        this._previous.addEventListener('click', () => move(-1), options);
        this._next.addEventListener('click', () => move(1), options);
        this._controls.append(this._previous, this._position, this._next);
        (this.closest('.clx-mainimg') || this._counter).insertAdjacentElement('afterend', this._controls);
      }
      let start;
      let dragged = false;
      this.addEventListener('pointerdown', event => { start = event.clientX; dragged = false; }, options);
      this.addEventListener('pointermove', event => { if (start != null && Math.abs(event.clientX - start) > 10) dragged = true; }, options);
      this._images.forEach(img => {
        img.tabIndex = 0;
        img.setAttribute('role', 'button');
        img.setAttribute('aria-label', lpmText('Ampliar imagen: ') + (img.alt || lpmText('joya')));
        img.addEventListener('click', () => { if (!dragged) this.openZoom(img); }, options);
        img.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); this.openZoom(img); }
        }, options);
      });
      update();
      this._initialized = true;
      // The parent PDP can select a variant before this deferred initialization.
      // Apply the latest choice after the initial measurement has completed.
      if (this._pendingSource) {
        const source = this._pendingSource;
        this._pendingSource = null;
        this.showImage(source);
      }
    }
    showImage(source) {
      if (!source) return;
      if (!this._initialized) {
        this._pendingSource = source;
        return;
      }
      // A filename is stable across Shopify's CDN host and width parameters.
      const key = value => new URL(value, document.baseURI).pathname.split('/').pop();
      const images = this._images || Array.from(this.querySelectorAll(':scope > img'));
      const target = images.find(image => key(image.src) === key(source));
      if (!target) return;
      this.showMedia(target);
    }
    showMedia(target) {
      if (!target) return;
      const version = this._mediaVersion = (this._mediaVersion || 0) + 1;
      (this._slides || this._images).forEach(slide => {
        slide.toggleAttribute('data-lpm-current', slide === target);
        if (slide !== target && slide.tagName === 'VIDEO') slide.pause();
      });
      if (target.tagName === 'IMG') target.loading = 'eager';
      else if (target.preload === 'none') { target.preload = 'metadata'; target.load(); }
      if (matchMedia('(max-width: 749px)').matches) {
        // SOPH: Firefox no cuenta en el scrollWidth las fotos lazy que aún no han cargado, así que
        // scrollTo se quedaba corto (el cambio de color aterrizaba en otra foto). Se cargan las
        // fotos hasta la de destino y se reintenta el scroll cuando la de destino carga.
        const index = (this._slides || this._images).indexOf(target);
        (this._slides || this._images).slice(0, index + 1).forEach(slide => { if (slide.tagName === 'IMG') slide.loading = 'eager'; });
        const go = () => {
          if (version !== this._mediaVersion) return;
          const offset = target.getBoundingClientRect().left - this.getBoundingClientRect().left + this.scrollLeft;
          this.scrollTo({ left: offset, behavior: 'instant' });
        };
        go();
        if (target.tagName === 'IMG' && !target.complete) target.addEventListener('load', go, { once: true });
        requestAnimationFrame(() => {
          if (version !== this._mediaVersion) return;
          go();
          this.updateHeight();
        });
      }
      this.updateHeight();
    }
    updateSources() {
      const img = this._images[0];
      const source = new URL(img.src, document.baseURI);
      if (!['https:', 'http:'].includes(source.protocol)) return;
      const srcset = [360, 540, 720, 900].map(width => {
        const url = new URL(source); url.searchParams.set('width', width);
        return `${url.href} ${width}w`;
      }).join(', ');
      if (img.getAttribute('srcset') !== srcset) img.setAttribute('srcset', srcset);
    }
    openZoom(img) {
      if (!this._dialog) {
        this._dialog = document.createElement('dialog');
        this._dialog.className = 'lpm-gallery-zoom';
        this._dialog.setAttribute('aria-label', lpmText('Imagen ampliada del producto'));
        const close = document.createElement('button');
        close.type = 'button'; close.textContent = lpmText('Cerrar');
        close.addEventListener('click', () => this._dialog.close());
        this._zoomImage = document.createElement('img');
        this._dialog.append(close, this._zoomImage);
        document.body.append(this._dialog);
      }
      const source = new URL(img.src, document.baseURI);
      if (['https:', 'http:'].includes(source.protocol)) source.searchParams.set('width', '1600');
      this._zoomImage.src = source.href;
      this._zoomImage.alt = img.alt;
      this._dialog.showModal();
    }
    updateHeight() {
      if (!matchMedia('(max-width: 749px)').matches) {
        this.style.removeProperty('--lpm-gallery-height');
        if (this._controls) {
          const current = Math.max(0, this._slides.findIndex(slide => slide.hasAttribute('data-lpm-current')));
          this._position.textContent = `${current + 1} / ${this._slides.length}`;
          this._previous.disabled = current === 0;
          this._next.disabled = current === this._slides.length - 1;
        }
        return;
      }
      const left = this.getBoundingClientRect().left;
      const active = this._slides.reduce((nearest, image) =>
        Math.abs(image.getBoundingClientRect().left - left) < Math.abs(nearest.getBoundingClientRect().left - left) ? image : nearest
      );
      const current = this._slides.indexOf(active);
      if (this._counter) this._counter.textContent = `${current + 1} / ${this._slides.length}` + (active.tagName === 'VIDEO' ? ' · Video' : lpmText(' · Toca para ampliar'));
      this.updatePagination(current);
      this._slides.forEach(slide => {
        slide.toggleAttribute('data-lpm-current', slide === active);
        if (slide !== active && slide.tagName === 'VIDEO') slide.pause();
      });
      const ratio = active.videoWidth / active.videoHeight || active.naturalWidth / active.naturalHeight || Number(active.getAttribute('width')) / Number(active.getAttribute('height')) || 1;
      const height = active.getBoundingClientRect().width / ratio;
      const previous = parseFloat(this.style.getPropertyValue('--lpm-gallery-height')) || 0;
      if (height > 0 && Math.abs(previous - height) > 0.1) {
        this.style.setProperty('--lpm-gallery-height', `${height}px`);
      }
    }
    updatePagination(current) {
      if (!this._pagination) return;
      if (this._paginationCurrent === current) return;
      this._paginationCurrent = current;
      const total = this._slides.length;
      const visible = Math.min(5, total);
      const start = Math.max(0, Math.min(current - 2, total - visible));
      this._pagination.replaceChildren(...Array.from({length:visible}, (_, slot) => {
        const index = start + slot;
        const dot = document.createElement('i');
        if (index === current) dot.setAttribute('aria-current', 'true');
        if ((slot === 0 && start > 0) || (slot === visible - 1 && start + visible < total)) dot.dataset.edge = '';
        return dot;
      }));
    }
  });
})();
